import { useEffect, useRef } from "react"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"
import { DateTime } from "luxon"

import useT from "utils/hooks/useT"
import { canSign } from "state_management/lims/segregationOfDuties"
import {
  limsResults,
  limsSampleQc,
  limsConsumables,
  limsReviews,
  sampleView,
  assets,
} from "state_management"

// QA Analysis (SPEC-070, Plannertech-user#4223) — the LIMS worklist of analyses whose results wait
// for a QA DECISION. One row per analysis with ≥1 result in "sent-to-qa", its count of samples
// awaiting the decision and when the oldest of them reached QA; the longest wait is on top
// (limsResults.selectQaAnalysisWorklist).
//
// History: SPEC-012 built this list, SPEC-022 (K5) made it a pure worklist that deep-linked to the
// plan, and SPEC-038 retired it when RELEASE became sample-level (QaSampleReview). Release stays
// there; this list is back for the decision step only, which still lives per analysis.
//
// The list has NO decision controls. "Open" opens the analysis-task dialog IN PLACE, over /lims, the
// way Sample View's "Go to analysis" does (RR8 #4061: AnalysisTaskHost answers the request on any
// route) — so QA comes back to the list when it closes. The decision itself is made in the dialog's
// QaDecisionSection, which gates it with canSign(…, {capability: "qa", four-eyes}) and the server.
// Everyone with LIMS sees the list (opening is a read); a user without `qa` gets a hint saying only
// QA can decide.
export default function QaAnalysisReview() {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const worklist = useSelector(limsResults.selectQaAnalysisWorklist, shallowEqual)
  const qaCapabilityGate = useSelector((s) => canSign(s, { capability: "qa" }), shallowEqual)
  const dialogOpen = useSelector((s) => s.visual.analysisTaskDialogOpen)

  useEffect(() => {
    // SPEC-021 (B3): QC capabilities ride on the member dump (loaded app-wide) — no fetch here.
    // `analysisTasks` (what "Open" resolves the analysis to) comes from the app bootstrap, which
    // /lims goes through like the plan does.
    dispatch(limsResults.fetch())
    dispatch(limsSampleQc.fetch())
    dispatch(limsConsumables.fetch())
    dispatch(assets.fetch())
    dispatch(sampleView.fetch())
  }, [dispatch])

  // Refresh when the dialog CLOSES, so an analysis decided there (or by a colleague meanwhile) drops
  // off. The decision already updates the store, so this is the belt to that — it also picks up
  // results sent to QA while the dialog was open.
  const wasDialogOpen = useRef(dialogOpen)
  useEffect(() => {
    if (wasDialogOpen.current && !dialogOpen) dispatch(limsResults.fetch())
    wasDialogOpen.current = dialogOpen
  }, [dialogOpen, dispatch])

  const fmtDate = (iso) => (iso ? DateTime.fromISO(iso).toFormat("dd/MM/yyyy HH:mm") : "—")

  // jumpToWeek:false — we stay on /lims; moving the plan's week would only trigger a schedule
  // refetch behind the dialog (see limsReviews.openReviewAnalysis).
  const openAnalysis = (analysisId) => {
    dispatch(limsReviews.openReviewAnalysis(analysisId, { jumpToWeek: false }))
  }

  return (
    <div className="LimsQueue">
      <h1 className="LimsQueue-title">{t("limsQaAnalysisReview")}</h1>
      {!qaCapabilityGate.allowed && <p className="LimsQueue-intro">{t("limsQaGhostCapability")}</p>}

      <h2 className="LimsQueue-subtitle">{t("limsQaReviewPending")}</h2>
      {worklist.length === 0 ? (
        <p className="LimsQueue-empty">{t("limsQaReviewEmpty")}</p>
      ) : (
        <Table striped hover responsive className="LimsQueue-table LimsQueue-table--narrow">
          <thead>
            <tr>
              <th>{t("limsQaReviewAnalysis")}</th>
              <th>{t("limsQaReviewPending")}</th>
              <th>{t("limsResultSentToQaShort")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {worklist.map((row) => (
              <tr key={row.analysisId || "none"}>
                <td>{row.analysisLabel || "—"}</td>
                <td>{row.pending}</td>
                <td>{fmtDate(row.waitingSince)}</td>
                <td className="LimsQueue-actions">
                  <div className="LimsQueue-actions-inner">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => openAnalysis(row.analysisId)}
                      disabled={!row.analysisId}
                    >
                      {t("limsQaReviewOpen")}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  )
}
